import { ViewMode, Task, TaskItem } from './types';
import {
  differenceInCalendarMonths,
  differenceInDays,
  differenceInHours,
  differenceInWeeks
} from 'date-fns';
import { formatInTimeZone } from 'date-fns-tz';

export const formatDate = (date: Date, timeZone?: string): string => {
  if (timeZone) {
    return formatInTimeZone(date, timeZone, 'MMM d');
  }
  return date.toLocaleDateString([], { month: 'short', day: 'numeric' });
};

export const formatDateWithYear = (date: Date, timeZone?: string): string => {
  if (timeZone) {
    return formatInTimeZone(date, timeZone, 'MMM d, yyyy');
  }
  return date.toLocaleDateString([], {
    month: 'short',
    day: 'numeric',
    year: 'numeric'
  });
};

export const getDateForPosition = (
  position: number,
  startDate: Date,
  hourWidth: number,
  viewMode: ViewMode
): Date => {
  const msPerHour = 1000 * 60 * 60;
  const msPerDay = msPerHour * 24;
  const result = new Date(startDate);

  switch (viewMode) {
    case 'hour': {
      // position = hoursFractional * hourWidth
      const hours = position / hourWidth;
      return new Date(startDate.getTime() + hours * msPerHour);
    }

    case 'day': {
      // position = floor(days) * hourWidth
      const days = Math.floor(position / hourWidth);
      result.setDate(result.getDate() + days);
      return result;
    }

    case 'week': {
      // position = floor(weeks) * hourWidth
      const weeks = Math.floor(position / hourWidth);
      result.setDate(result.getDate() + weeks * 7);
      return result;
    }

    case 'month': {
      // position = floor(months) * hourWidth
      const months = Math.floor(position / hourWidth);
      // preserves day-of-month; JS will auto-rollover if needed
      result.setMonth(result.getMonth() + months);
      return result;
    }

    default: {
      // fallback to continuous hours (like 'hour' mode)
      const hours = position / hourWidth;
      return new Date(startDate.getTime() + hours * msPerHour);
    }
  }
};

export const getPositionForDate = (
  date: Date | undefined,
  startDate: Date,
  itemWidth: number,
  viewMode: ViewMode
): number => {
  if (!date) return 0;

  const diffMs = date.getTime() - startDate.getTime();

  switch (viewMode) {
    case 'hour': {
      // itemWidth = width of 1 hour
      const hoursFractional = diffMs / (1000 * 60 * 60);
      return hoursFractional * itemWidth;
    }

    case 'day': {
      // itemWidth = width of 24 hours (i.e. one full day)
      // use fractional days so time‐of‐day is included
      const daysFractional = diffMs / (1000 * 60 * 60 * 24);
      return daysFractional * itemWidth;
    }

    case 'week': {
      // itemWidth = width of 7 days (i.e. one full week)
      // use fractional weeks so time‐of‐week is included
      const weeksFractional = diffMs / (1000 * 60 * 60 * 24 * 7);
      return weeksFractional * itemWidth;
    }

    case 'month': {
      // itemWidth = width of 1 month (varying length)
      let months =
        (date.getFullYear() - startDate.getFullYear()) * 12 +
        (date.getMonth() - startDate.getMonth());
      if (date.getDate() < startDate.getDate()) {
        months--;
      }
      return months * itemWidth;
    }

    default: {
      // fallback: treat as 'hour'
      const hoursFractional = diffMs / (1000 * 60 * 60);
      return hoursFractional * itemWidth;
    }
  }
};

export const getTaskStyle = (
  task: TaskItem,
  startDate: Date,
  hourWidth: number,
  viewMode: ViewMode
): {
  left: number;
  width: number;
  backgroundColor?: string;
} => {
  if (!task?.start || !task?.end) {
    return {
      left: 0,
      width: hourWidth / 4 // Minimum width for invalid tasks
    };
  }

  const left = getPositionForDate(task.start, startDate, hourWidth, viewMode);
  const width = getPositionForDate(task.end, task.start, hourWidth, viewMode);

  return {
    left,
    width: Math.max(width, hourWidth / 4) // Ensure minimum width for very short tasks
  };
};

export const flattenTasks = (tasks: Task[]): Task[] => {
  const flattened: Task[] = [];

  const flatten = (taskList: Task[], level = 0) => {
    taskList.forEach((task) => {
      flattened.push({ ...task, level });

      if (task.children && !task.collapsed) {
        flatten(task.children, level + 1);
      }
    });
  };

  flatten(tasks);
  return flattened;
};

const diffFunctions: Record<ViewMode, (later: Date, earlier: Date) => number> =
  {
    hour: differenceInHours,
    day: differenceInDays,
    week: differenceInWeeks,
    month: differenceInCalendarMonths
  };

export function getTotalWidth(
  viewMode: ViewMode,
  startDate: Date,
  endDate: Date,
  slotWidth: number
): number {
  if (endDate < startDate) {
    return 0;
  }
  const count = diffFunctions[viewMode](endDate, startDate) + 1;
  return count * slotWidth;
}

export const getTimezoneLabel = (timeZone: string, deviceTimeZone: string): string => {
  if (!timeZone) return '';
  if (timeZone === deviceTimeZone) return '';
  
  const now = new Date();
  
  // Get UTC offset string (e.g., '-05:00' or '+03:00')
  const offsetString = formatInTimeZone(now, timeZone, 'XXX');
  
  // Parse offset string to get hours and minutes
  // Format is '+HH:MM' or '-HH:MM'
  const match = offsetString.match(/^([+-])(\d{2}):(\d{2})$/);
  if (!match) return '';
  
  const sign = match[1] === '+' ? '+' : '-';
  const hours = Number.parseInt(match[2], 10);
  const minutes = Number.parseInt(match[3], 10);
  
  // Include minutes if non-zero, otherwise just show hours
  if (minutes === 0) {
    return `GMT ${sign}${hours}`;
  }
  
  return `GMT ${sign}${hours}:${minutes}`;
};
